import { useEffect, useState } from 'react';
import { Box, IconButton, Skeleton, TextField, Tooltip, Typography } from '@mui/material';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import EditLocationAltOutlinedIcon from '@mui/icons-material/EditLocationAltOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

import {
  IPrevisaoTempo,
  ISeveridadeAlerta,
  PrevisaoTempoService,
} from '../../shared/services/api/mercado';
import { ICoresCabecalho } from './coresCabecalho';
import { IconeCondicao } from './IconeCondicao';
import { DialogPrevisaoTempo } from './DialogPrevisaoTempo';

const ICONE_ALERTA: Record<ISeveridadeAlerta, JSX.Element> = {
  perigo: <ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />,
  atencao: <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />,
  positivo: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16 }} />,
};

const nomeDoDia = (dataIso: string, indice: number) => {
  if (indice === 0) return 'Hoje';
  if (indice === 1) return 'Amanhã';
  return new Date(`${dataIso}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
};

// Cliques nos controles (trocar cidade, atualizar, campo de busca) não podem
// abrir a previsão detalhada — o painel inteiro é clicável.
const naoPropagar = (e: React.SyntheticEvent) => e.stopPropagation();

interface IPrevisaoTempoProps {
  cores: ICoresCabecalho;
}

// Painel de previsão do tempo encaixado no cabeçalho do painel inicial —
// as cores vêm do cabeçalho (escuro ou claro, conforme o tema). Clicar no
// painel abre a previsão detalhada num dialog (DialogPrevisaoTempo), que
// tem o link pro site de clima.
export const PrevisaoTempo: React.FC<IPrevisaoTempoProps> = ({ cores }) => {
  const [previsao, setPrevisao] = useState<IPrevisaoTempo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editandoCidade, setEditandoCidade] = useState(false);
  const [cidadeDigitada, setCidadeDigitada] = useState('');
  const [buscandoCidade, setBuscandoCidade] = useState(false);
  const [erroCidade, setErroCidade] = useState<string | null>(null);
  const [detalheAberto, setDetalheAberto] = useState(false);

  const carregar = () => {
    setCarregando(true);
    setErro(null);
    PrevisaoTempoService.obterPrevisaoTempo()
      .then(setPrevisao)
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  };

  const buscarCidade = () => {
    if (!cidadeDigitada.trim()) return;
    setBuscandoCidade(true);
    setErroCidade(null);
    PrevisaoTempoService.definirLocalizacaoManual(cidadeDigitada.trim())
      .then(resultado => {
        setPrevisao(resultado);
        setErro(null);
        setEditandoCidade(false);
        setCidadeDigitada('');
      })
      .catch((e: Error) => setErroCidade(e.message))
      .finally(() => setBuscandoCidade(false));
  };

  useEffect(() => {
    carregar();
  }, []);

  const painelSx = {
    height: '100%',
    boxSizing: 'border-box' as const,
    borderRadius: 3,
    p: 1.5,
    color: cores.texto,
    bgcolor: cores.painelFundo,
    backdropFilter: 'blur(8px)',
    border: `1px solid ${cores.painelBorda}`,
  };

  const botaoSx = { p: 0.25, color: cores.textoSecundario, '&:hover': { color: cores.texto } };

  const campoCidade = (
    <Box display="flex" gap={1} alignItems="flex-start" mt={1} onClick={naoPropagar}>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder="Ex: Barreiras - BA"
        value={cidadeDigitada}
        onChange={e => setCidadeDigitada(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && buscarCidade()}
        error={!!erroCidade}
        helperText={erroCidade}
        sx={{
          '& .MuiOutlinedInput-root': { color: cores.texto, bgcolor: cores.campoFundo },
          '& fieldset': { borderColor: cores.campoBorda },
          '& .MuiFormHelperText-root': { color: cores.erroCampo },
        }}
      />
      <IconButton
        onClick={buscarCidade}
        disabled={buscandoCidade}
        sx={{ border: `1px solid ${cores.campoBorda}`, height: 40, width: 40, color: cores.texto }}
        aria-label="Buscar cidade"
      >
        <SearchRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  if (carregando) {
    return (
      <Box sx={painelSx} display="flex" gap={1.5}>
        <Skeleton variant="rounded" width={150} height={92} sx={{ bgcolor: cores.painelBorda }} />
        <Box flex={1} display="grid" gap={1} sx={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={92} sx={{ bgcolor: cores.painelBorda }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (erro || !previsao) {
    return (
      <Box sx={painelSx} display="flex" flexDirection="column" justifyContent="center">
        <Typography variant="body2" sx={{ color: cores.textoSecundario }}>{erro || 'Previsão do tempo indisponível.'}</Typography>
        <Box display="flex" gap={2} mt={0.5}>
          <Typography variant="body2" fontWeight={600} onClick={carregar} sx={{ color: cores.destaque, cursor: 'pointer' }}>
            Tentar novamente
          </Typography>
          <Typography variant="body2" fontWeight={600} onClick={() => setEditandoCidade(v => !v)} sx={{ color: cores.destaque, cursor: 'pointer' }}>
            Digitar minha cidade
          </Typography>
        </Box>
        {editandoCidade && campoCidade}
      </Box>
    );
  }

  const alerta = previsao.alertas.find(a => a.severidade === 'perigo') ?? previsao.alertas[0];
  const abrirDetalhe = () => setDetalheAberto(true);

  return (
    <>
    <Box
      sx={{
        ...painelSx,
        cursor: 'pointer',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': { bgcolor: cores.painelHover, borderColor: cores.destaque },
        '&:hover .abrir-previsao': { opacity: 1 },
      }}
      display="flex"
      flexDirection="column"
      gap={1}
      role="button"
      tabIndex={0}
      title="Ver a previsão do tempo detalhada"
      onClick={abrirDetalhe}
      onKeyDown={e => e.key === 'Enter' && e.target === e.currentTarget && abrirDetalhe()}
    >
      <Box display="flex" gap={1.5} alignItems="stretch" flexWrap="wrap">
        {/* Agora */}
        <Box flex="1 1 150px" minWidth={0} display="flex" flexDirection="column" justifyContent="space-between">
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
            <Box display="flex" alignItems="center" gap={0.25} minWidth={0}>
              <RoomOutlinedIcon sx={{ fontSize: 15, color: cores.destaque, flexShrink: 0 }} />
              <Typography
                variant="caption"
                fontWeight={700}
                noWrap
                title={previsao.origemLocalizacao === 'ip' ? `${previsao.cidade} (localização aproximada pela conexão — use "Trocar cidade" para ajustar)` : previsao.cidade}
              >
                {previsao.cidade}{previsao.origemLocalizacao === 'ip' ? ' (aprox.)' : ''}
              </Typography>
            </Box>
            <Box display="flex" flexShrink={0} alignItems="center" onClick={naoPropagar}>
              <OpenInFullRoundedIcon
                className="abrir-previsao"
                sx={{ fontSize: 13, color: cores.destaque, opacity: 0, transition: 'opacity 150ms ease', mr: 0.5 }}
              />
              <Tooltip title="Trocar cidade">
                <IconButton size="small" onClick={() => setEditandoCidade(v => !v)} sx={botaoSx}>
                  <EditLocationAltOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Atualizar">
                <IconButton size="small" onClick={carregar} sx={botaoSx}>
                  <RefreshRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1} my={0.5}>
            <IconeCondicao icone={previsao.atual.condicao.icone} tamanho={38} cores={cores.condicao} />
            <Box minWidth={0}>
              <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{Math.round(previsao.atual.temperatura)}°C</Typography>
              <Typography variant="caption" noWrap display="block" sx={{ color: cores.textoSecundario }}>{previsao.atual.condicao.texto}</Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1.5} sx={{ color: cores.textoSecundario }}>
            <Box display="flex" alignItems="center" gap={0.4}>
              <AirOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">{Math.round(previsao.atual.velocidadeVento)} km/h</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.4}>
              <WaterDropOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">{Math.round(previsao.atual.umidade)}%</Typography>
            </Box>
          </Box>
        </Box>

        {/* Próximos dias */}
        <Box flex="2 1 240px" minWidth={0} display="grid" gap={0.75} sx={{ gridTemplateColumns: `repeat(${previsao.diaria.length}, 1fr)` }}>
          {previsao.diaria.map((dia, indice) => (
            <Box
              key={dia.data}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={0.25}
              py={0.75}
              borderRadius={2}
              sx={{ bgcolor: indice === 0 ? cores.destaqueFundo : 'transparent' }}
            >
              <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'capitalize', color: indice === 0 ? cores.destaque : cores.textoSecundario }}>
                {nomeDoDia(dia.data, indice)}
              </Typography>
              <IconeCondicao icone={dia.condicao.icone} tamanho={22} cores={cores.condicao} />
              <Typography variant="caption" fontWeight={700} lineHeight={1.2}>
                {Math.round(dia.tempMax)}° <Box component="span" sx={{ color: cores.textoSecundario, fontWeight: 400 }}>{Math.round(dia.tempMin)}°</Box>
              </Typography>
              <Typography variant="caption" sx={{ color: cores.chuva, lineHeight: 1.2 }}>
                {Math.round(dia.probabilidadeChuva)}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {editandoCidade && campoCidade}

      {alerta && (
        <Box display="flex" alignItems="center" gap={0.75} sx={{ color: cores.alerta[alerta.severidade] }}>
          {ICONE_ALERTA[alerta.severidade]}
          <Typography variant="caption" fontWeight={600} noWrap title={alerta.mensagem}>
            {alerta.titulo}
            <Box component="span" sx={{ fontWeight: 400, color: cores.textoSecundario }}> — {alerta.mensagem}</Box>
          </Typography>
        </Box>
      )}
    </Box>
    {/* Fora do Box clicável: o clique dentro do dialog (portal) sobe pela
        árvore do React e reabriria o dialog ao fechar. */}
    <DialogPrevisaoTempo open={detalheAberto} onClose={() => setDetalheAberto(false)} previsao={previsao} cores={cores} />
    </>
  );
};
